import React, { useState, useEffect } from 'react';
import { Typography, Button, Table, Form, DatePicker, Space, Tooltip } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './fixLog.css';
import FIXMessageLogService from '../../services/FIXMessageLogService';
import { convertToLocal } from '../../components/Functions/convertToLocal';
import dayjs from 'dayjs';
import { objectToQueryString } from '../../util/Common';
import FormItem from 'antd/es/form/FormItem';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const FixExecutionOrderLog = () => {
  const location = useLocation();
  const [executionOrderList, setExecutionOrderList] = useState([]);
  const navigate = useNavigate();
  const { showNotification } = useAuth();
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const queryParams = new URLSearchParams(location.search);

  const startTimestamp = Number(queryParams.get('startDate')) || null;
  const endTimestamp = Number(queryParams.get('endDate')) || null;

  const startDate = startTimestamp ? dayjs(startTimestamp * 1000) : null;
  const endDate = endTimestamp ? dayjs(endTimestamp * 1000) : null;

  const page = Number(queryParams.get('page')) || 1;
  const size = Number(queryParams.get('size')) || 10;

  const loadData = async () => {
    const payload = {
      startDate: startTimestamp,
      endDate: endTimestamp,
      page: page,
      size: size,
    };
    setLoading(true);
    const result = await FIXMessageLogService.getFIXExecutionOrderLog(payload);
    setLoading(false);
    if (result.success) {
      setExecutionOrderList(result.executionOrderReportList);
      setTotalCount(result.totalCount);
    } else {
      showNotification('error', result.message);
    }
  };

  useEffect(() => {
    loadData(); // Initial load with null startDate and endDate
  }, [location.search]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const formLayout = windowWidth <= 1024 ? 'vertical' : 'inline';

  const handleRefresh = () => {
    navigate('/reports/fix-execution-order-log');
    form.setFieldsValue({
      selectDates: [null, null],
      messageType: null,
    });
  };

  const onFinish = async formValues => {
    let startDateTimestamp = null;
    let endDateTimestamp = null;

    if (formValues.selectDates && formValues.selectDates[0]) {
      startDateTimestamp = formValues.selectDates[0].startOf('day').valueOf();
    }
    if (formValues.selectDates && formValues.selectDates[1]) {
      endDateTimestamp = formValues.selectDates[1].endOf('day').valueOf();
    }

    let payload = {
      startDate: startDateTimestamp !== null ? Math.floor(startDateTimestamp / 1000) : null,
      endDate: endDateTimestamp !== null ? Math.floor(endDateTimestamp / 1000) : null,
      page: 1,
      size: 10,
    };

    const queryString = objectToQueryString(payload);
    navigate(`/reports/fix-execution-order-log?` + queryString);
  };

  // Table columns
  const columns = [
    {
      title: 'Report ID',
      dataIndex: 'fixExecutionOrderReportId',
      key: 'fixExecutionOrderReportId',
    },
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
    },
    {
      title: 'Average Px',
      dataIndex: 'avgPx',
      key: 'avgPx',
      render: text => text.toLocaleString('en-US'),
    },
    {
      title: 'CL Order ID',
      dataIndex: 'clOrderId',
      key: 'clOrderId',
    },
    {
      title: 'CUM Quantity',
      dataIndex: 'cumQty',
      key: 'cumQty',
      render: text => text.toLocaleString('en-US'),
    },
    {
      title: 'Execution Type',
      dataIndex: 'execType',
      key: 'execType',
    },
    {
      title: 'Execution ID',
      dataIndex: 'execId',
      key: 'execId',
    },
    {
      title: 'Execution Transaction Type',
      dataIndex: 'execTransType',
      key: 'execTransType',
    },
    {
      title: 'ID Source',
      dataIndex: 'idSource',
      key: 'idSource',
    },
    {
      title: 'Last Px',
      dataIndex: 'lastPx',
      key: 'lastPx',
      render: text => text.toLocaleString('en-US'),
    },
    {
      title: 'Last Shares',
      dataIndex: 'lastShares',
      key: 'lastShares',
    },
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
    },
    {
      title: 'Order Quantity',
      dataIndex: 'orderQty',
      key: 'orderQty',
      render: text => text.toLocaleString('en-US'),
    },
    {
      title: 'Order Status',
      dataIndex: 'ordStatus',
      key: 'ordStatus',
    },
    {
      title: 'Order Type',
      dataIndex: 'ordType',
      key: 'ordType',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: text => text.toLocaleString('en-US'),
    },
    {
      title: 'Rule 80A',
      dataIndex: 'rule80A',
      key: 'rule80A',
    },
    {
      title: 'Security ID',
      dataIndex: 'securityId',
      key: 'securityId',
    },
    {
      title: 'Side',
      dataIndex: 'side',
      key: 'side',
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
    },
    {
      title: 'Text',
      dataIndex: 'text',
      key: 'text',
    },
    {
      title: 'Time In Force',
      dataIndex: 'timeInForce',
      key: 'timeInForce',
    },
    {
      title: 'Transaction Time',
      dataIndex: 'transactTime',
      key: 'transactTime',
      render: (_, record) => convertToLocal(record.transactTime),
    },
    {
      title: 'Client ID',
      dataIndex: 'clientId',
      key: 'clientId',
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
    },
    {
      title: 'Leave Quantity',
      dataIndex: 'leavesQty',
      key: 'leavesQty',
      render: text => text.toLocaleString('en-US'),
    },
    {
      title: 'Security Type',
      dataIndex: 'securityType',
      key: 'securityType',
    },
    {
      title: 'Order Reject Reason',
      dataIndex: 'orderRejectReason',
      key: 'orderRejectReason',
    },
  ];

  return (
    <>
      <Title level={4} style={{ marginTop: 0 }}>
        Fix Execution Order Log
      </Title>
      <Form
        onFinish={onFinish}
        form={form}
        className="right-aligned-form"
        layout={formLayout}
        initialValues={{
          selectDates: [startDate, endDate],
        }}
      >
        <Form.Item label="Select Dates" name={'selectDates'}>
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>
        <Space style={{ float: 'right' }}>
          <Tooltip title="Clear fields">
            <Button
              type="default"
              size={'middle'}
              icon={<ClearOutlined />}
              onClick={handleRefresh}
            ></Button>
          </Tooltip>
          <Button type="primary" size="middle" htmlType="submit" key={'submit'}>
            Search
          </Button>
        </Space>
      </Form>
      <Table
        loading={loading}
        style={{ paddingTop: '1rem' }}
        columns={columns}
        dataSource={executionOrderList.map(log => ({ ...log, key: log.fixExecutionOrderReportId }))}
        size={'large'}
        bordered
        scroll={{
          x: 'max-content',
        }}
        pagination={{
          current: page,
          pageSize: size,
          total: totalCount,
        }}
        onChange={(pagination, filters, sorter) => {
          const formValues = {
            startDate: startTimestamp,
            endDate: endTimestamp,
            page: pagination.current,
            size: pagination.pageSize,
          };

          const queryString = objectToQueryString(formValues);
          navigate(`/reports/fix-execution-order-log?` + queryString);
        }}
      />
    </>
  );
};

export default FixExecutionOrderLog;
